import {
    AwardIcon,
    Building2Icon,
    BriefcaseIcon,
    CalendarDaysIcon,
    CalendarXIcon,
    ChartColumnIcon,
    ChartGantt,
    ChartLineIcon,
    ChartPieIcon,
    CircleHelpIcon,
    ClockAlertIcon,
    ClockIcon,
    FileTextIcon,
    FileXIcon,
    GlobeIcon,
    HandshakeIcon,
    HistoryIcon,
    HourglassIcon,
    InboxIcon,
    ListChecksIcon,
    MessageCircleIcon,
    MessageCircleQuestionMarkIcon,
    PencilLineIcon,
    PhoneCallIcon,
    ReceiptIcon,
    ReceiptTextIcon,
    RefreshCwIcon,
    ScaleIcon,
    SearchIcon,
    ShieldIcon,
    SvgIconComponent,
    TagIcon,
    TargetIcon,
    TimerIcon,
    TrophyIcon,
    UserSearchIcon,
    UsersIcon,
    UserXIcon,
    WalletIcon,
} from 'components/icons';
import { FamilyId, familyTileClass } from 'utils/families';
import { PlaceholderFillConfig } from '../hooks/usePlaceholderFill';
import {
    getSamplePrompts,
    isNew,
    LOOKBACK_OPTIONS,
    LOOKBACK_OPTIONS_STANDARD,
    OVERDUE_DAYS_OPTIONS,
    PromptBadge,
    PromptOption,
    TIME_PERIOD_OPTIONS_FULL,
    TIME_PERIOD_OPTIONS_MONTH,
} from '../samplePrompts';
import { PageObjectType } from './usePageContext';

// `ai-chat.floating.prompts.<type>.<n>` — text lives in en.json, listed in display order. `<type>.<n>`
// is also the prompt's PostHog `prompt_id`, so a retired prompt's number is never reused: a
// replacement takes the next free one.
const keysFor = (type: string, ...numbers: number[]): string[] =>
    numbers.map((number) => `ai-chat.floating.prompts.${type}.${number}`);

// Per-page starter prompts, keyed by page type. Pages without an entry fall back to
// DEFAULT_PROMPT_KEYS. Selecting a prompt fills the composer (it is not sent automatically); the
// page context is attached automatically by the chat controller once the user does send it.
const PAGE_PROMPT_KEYS: Partial<Record<PageObjectType, string[]>> = {
    dashboard: keysFor('dashboard', 1, 8, 2, 3, 4, 5, 6, 7, 9),
    contact: keysFor('contact', 1, 2, 11, 4, 5, 7, 8, 9, 10),
    matter: keysFor('matter', 2, 3, 4, 5, 6, 7, 8, 9, 10, 11),
    invoice: keysFor('invoice', 1, 8, 2, 5, 6, 7),
    contacts: keysFor('contacts', 7, 1, 2, 3, 4, 5, 6, 8),
    matters: keysFor('matters', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12),
    invoices: keysFor('invoices', 1, 2, 3, 4, 5),
    timecard: keysFor('timecard', 1, 2, 4),
    timecards: keysFor('timecards', 1, 2, 3, 4, 5, 6),
    effort: keysFor('effort', 1, 5, 8, 7, 3, 4),
    efforts: keysFor('efforts', 5, 1, 2, 3, 4, 6),
    employee: keysFor('employee', 1, 2, 5, 6, 7),
    employees: keysFor('employees', 1, 2, 3),
    group: keysFor('group', 1, 2, 3, 4, 5, 6),
    groups: keysFor('groups', 1),
    team: keysFor('team', 4, 2, 3),
    teams: keysFor('teams', 5, 1, 2, 4),
    office: keysFor('office', 1, 5, 2, 4, 6),
    offices: keysFor('offices', 2, 3, 4),
    'practice-area': keysFor('practice-area', 1, 2, 3, 4, 5),
    'practice-areas': keysFor('practice-areas', 2, 3),
    'job-role': keysFor('job-role', 1, 2, 3, 4),
    'job-roles': keysFor('job-roles', 2),
    'job-position': keysFor('job-position', 1, 2, 3, 4),
    'job-positions': keysFor('job-positions', 2),
    pricelists: keysFor('pricelists', 1),
    'experience-record': keysFor('experience-record', 1),
    'experience-records': keysFor('experience-records', 1, 2, 3),
};

// Every page type in PageObjectType is curated. The org-structure prompts stay within what the tools
// can reach — the employee tool bridges from a resolved office, team, job role or job position, the
// case tool from a resolved practice area, timecard aggregation filters by case-handling office and
// groups by job role or handling-office country, and case aggregation counts cases by office or practice
// area — so none of them promise a query the assistant would have to refuse.

export const DEFAULT_PROMPT_KEYS = ['ai-chat.floating.prompts.default.1'];

/** Returns the suggested-prompt i18n keys for the given page type, or the generic fallback. */
export const getPromptKeys = (objectType?: PageObjectType): string[] =>
    (objectType && PAGE_PROMPT_KEYS[objectType]) || DEFAULT_PROMPT_KEYS;

const PROMPT_KEY_PREFIX = 'ai-chat.floating.prompts.';

/** A listed prompt's id, `<type>.<n>`: its PostHog `prompt_id`, and the prompt id a chat it starts keeps. */
export const getPromptId = (promptKey: string): string => promptKey.replace(PROMPT_KEY_PREFIX, '');

// Each listed prompt's icon, and the family of the record it is about for its tile (none: the neutral tile, as for
// timecards and tasks). Keyed by prompt id; a retired prompt leaves the list and this map together.
const PROMPT_TILES: Record<string, readonly [SvgIconComponent, FamilyId?]> = {
    'dashboard.1': [CalendarXIcon],
    'dashboard.8': [PhoneCallIcon, 'contacts'],
    'dashboard.2': [FileTextIcon],
    'dashboard.3': [ClockIcon],
    'dashboard.4': [ChartColumnIcon, 'reports'],
    'dashboard.5': [TrophyIcon, 'cases'],
    'dashboard.6': [ListChecksIcon],
    'dashboard.7': [InboxIcon, 'cases'],
    'dashboard.9': [ChartGantt, 'contacts'],
    'contact.1': [UserSearchIcon, 'contacts'],
    'contact.2': [CalendarDaysIcon, 'bd'],
    'contact.11': [ChartGantt, 'contacts'],
    'contact.4': [HandshakeIcon, 'bd'],
    'contact.5': [WalletIcon, 'finance'],
    'contact.7': [TagIcon, 'finance'],
    'contact.8': [ListChecksIcon],
    'contact.9': [AwardIcon, 'bd'],
    'contact.10': [BriefcaseIcon, 'cases'],
    'matter.2': [RefreshCwIcon, 'contacts'],
    'matter.3': [FileTextIcon, 'cases'],
    'matter.4': [ReceiptIcon, 'finance'],
    'matter.5': [UsersIcon, 'people'],
    'matter.6': [ChartColumnIcon],
    'matter.7': [TagIcon, 'finance'],
    'matter.8': [HistoryIcon],
    'matter.9': [ListChecksIcon],
    'matter.10': [AwardIcon, 'bd'],
    'matter.11': [BriefcaseIcon, 'cases'],
    'invoice.1': [FileTextIcon, 'finance'],
    'invoice.8': [ReceiptIcon, 'finance'],
    'invoice.2': [ClockAlertIcon, 'finance'],
    'invoice.5': [WalletIcon, 'finance'],
    'invoice.6': [ReceiptTextIcon, 'finance'],
    'invoice.7': [TimerIcon, 'finance'],
    'contacts.7': [PhoneCallIcon, 'contacts'],
    'contacts.1': [UserXIcon, 'contacts'],
    'contacts.2': [TrophyIcon, 'contacts'],
    'contacts.3': [FileXIcon, 'finance'],
    'contacts.4': [ClockAlertIcon, 'finance'],
    'contacts.5': [ReceiptIcon, 'finance'],
    'contacts.6': [Building2Icon, 'contacts'],
    'contacts.8': [ChartGantt, 'contacts'],
    'matters.1': [BriefcaseIcon, 'cases'],
    'matters.2': [HourglassIcon, 'cases'],
    'matters.3': [ReceiptIcon, 'finance'],
    'matters.4': [TrophyIcon, 'cases'],
    'matters.5': [InboxIcon, 'cases'],
    'matters.6': [ChartColumnIcon],
    'matters.7': [SearchIcon, 'cases'],
    'matters.8': [SearchIcon, 'bd'],
    'matters.9': [AwardIcon, 'bd'],
    'matters.10': [ChartLineIcon, 'reports'],
    'matters.11': [ChartPieIcon, 'reports'],
    'matters.12': [UsersIcon, 'people'],
    'invoices.1': [ClockAlertIcon, 'finance'],
    'invoices.2': [TrophyIcon, 'contacts'],
    'invoices.3': [ReceiptTextIcon, 'finance'],
    'invoices.4': [WalletIcon, 'finance'],
    'invoices.5': [ReceiptIcon, 'finance'],
    'timecard.1': [PencilLineIcon],
    'timecard.2': [FileTextIcon],
    'timecard.4': [ReceiptIcon, 'finance'],
    'timecards.1': [CalendarXIcon],
    'timecards.2': [TimerIcon],
    'timecards.3': [ReceiptIcon, 'finance'],
    'timecards.4': [ClockIcon],
    'timecards.5': [ChartColumnIcon, 'reports'],
    'timecards.6': [HistoryIcon],
    'effort.1': [FileTextIcon, 'bd'],
    'effort.5': [TargetIcon, 'bd'],
    'effort.8': [ChartGantt, 'contacts'],
    'effort.7': [BriefcaseIcon, 'cases'],
    'effort.3': [MessageCircleIcon, 'bd'],
    'effort.4': [HandshakeIcon, 'bd'],
    'efforts.5': [PhoneCallIcon, 'contacts'],
    'efforts.1': [TimerIcon, 'bd'],
    'efforts.2': [UserXIcon, 'contacts'],
    'efforts.3': [TrophyIcon, 'people'],
    'efforts.4': [HandshakeIcon, 'bd'],
    'efforts.6': [ChartGantt, 'contacts'],
    'employee.1': [FileTextIcon, 'people'],
    'employee.2': [BriefcaseIcon, 'cases'],
    'employee.5': [TimerIcon],
    'employee.6': [TrophyIcon, 'contacts'],
    'employee.7': [ScaleIcon, 'cases'],
    'employees.1': [TrophyIcon, 'people'],
    'employees.2': [ChartColumnIcon, 'reports'],
    'employees.3': [CalendarXIcon],
    'group.1': [UsersIcon, 'contacts'],
    'group.2': [FileTextIcon, 'contacts'],
    'group.3': [ClockAlertIcon, 'finance'],
    'group.4': [UserXIcon, 'contacts'],
    'group.5': [CalendarDaysIcon, 'bd'],
    'group.6': [ChartGantt, 'contacts'],
    'groups.1': [UsersIcon, 'bd'],
    'team.4': [UsersIcon, 'people'],
    'team.2': [TimerIcon],
    'team.3': [HandshakeIcon, 'bd'],
    'teams.5': [Building2Icon, 'people'],
    'teams.1': [TrophyIcon, 'people'],
    'teams.2': [CalendarXIcon],
    'teams.4': [ChartColumnIcon, 'reports'],
    'office.1': [UsersIcon, 'people'],
    'office.5': [Building2Icon, 'people'],
    'office.2': [BriefcaseIcon, 'cases'],
    'office.4': [TimerIcon],
    'office.6': [InboxIcon, 'cases'],
    'offices.2': [ChartColumnIcon, 'reports'],
    'offices.3': [GlobeIcon, 'reports'],
    'offices.4': [InboxIcon, 'cases'],
    'practice-area.1': [FileTextIcon, 'people'],
    'practice-area.2': [BriefcaseIcon, 'cases'],
    'practice-area.3': [UsersIcon, 'people'],
    'practice-area.4': [ShieldIcon, 'compliance'],
    'practice-area.5': [ChartColumnIcon, 'reports'],
    'practice-areas.2': [ShieldIcon, 'compliance'],
    'practice-areas.3': [ChartColumnIcon, 'reports'],
    'job-role.1': [FileTextIcon],
    'job-role.2': [UsersIcon, 'people'],
    'job-role.3': [HistoryIcon, 'people'],
    'job-role.4': [CircleHelpIcon],
    'job-roles.2': [ChartColumnIcon, 'reports'],
    'job-position.1': [FileTextIcon],
    'job-position.2': [UsersIcon, 'people'],
    'job-position.3': [Building2Icon, 'people'],
    'job-position.4': [CircleHelpIcon],
    'job-positions.2': [ChartPieIcon, 'reports'],
    'pricelists.1': [TagIcon, 'finance'],
    'experience-record.1': [SearchIcon, 'bd'],
    'experience-records.1': [SearchIcon, 'bd'],
    'experience-records.2': [GlobeIcon, 'bd'],
    'experience-records.3': [AwardIcon, 'bd'],
    'default.1': [MessageCircleQuestionMarkIcon],
};

/** A listed prompt's row tile: its own icon, on the family pair of the record it is about. */
export const getPromptTile = (promptKey: string): { icon: SvgIconComponent; tileClass: string } => {
    const [icon, family] = PROMPT_TILES[getPromptId(promptKey)];
    return { icon, tileClass: familyTileClass(family) };
};

/**
 * The icon of the ready-made prompt a chat started from, by the id it was sent with: a page prompt's `id` or a panel
 * prompt's `<type>.<n>`. Nothing for an id no list holds any more, a retired prompt.
 */
export const promptIconFor = (promptId: string): SvgIconComponent | undefined =>
    getSamplePrompts().find((prompt) => prompt.id === promptId)?.icon ?? PROMPT_TILES[promptId]?.[0];

// Floating prompts carrying the New badge, keyed to the day it drops (see `isNew`).
const NEW_UNTIL: Record<string, string> = {
    'ai-chat.floating.prompts.experience-records.1': '2026-10-30',
    'ai-chat.floating.prompts.experience-records.2': '2026-10-30',
    'ai-chat.floating.prompts.experience-records.3': '2026-10-30',
    'ai-chat.floating.prompts.experience-record.1': '2026-10-30',
    'ai-chat.floating.prompts.matters.8': '2026-10-30',
    'ai-chat.floating.prompts.matters.9': '2026-10-30',
    'ai-chat.floating.prompts.matter.10': '2026-10-30',
    'ai-chat.floating.prompts.contact.9': '2026-10-30',
    'ai-chat.floating.prompts.dashboard.6': '2026-10-30',
    'ai-chat.floating.prompts.contact.8': '2026-10-30',
    'ai-chat.floating.prompts.matter.9': '2026-10-30',
    'ai-chat.floating.prompts.matters.10': '2026-10-30',
    'ai-chat.floating.prompts.matters.11': '2026-10-30',
    'ai-chat.floating.prompts.matters.12': '2026-10-30',
    'ai-chat.floating.prompts.matter.11': '2026-10-30',
    'ai-chat.floating.prompts.contact.10': '2026-10-30',
    'ai-chat.floating.prompts.contacts.6': '2026-10-30',
    'ai-chat.floating.prompts.dashboard.7': '2026-10-30',
    'ai-chat.floating.prompts.practice-area.5': '2026-10-30',
    'ai-chat.floating.prompts.practice-areas.3': '2026-10-30',
    'ai-chat.floating.prompts.office.6': '2026-10-30',
    'ai-chat.floating.prompts.offices.4': '2026-10-30',
    'ai-chat.floating.prompts.employee.7': '2026-10-30',
    'ai-chat.floating.prompts.invoice.8': '2026-10-30',
    'ai-chat.floating.prompts.invoices.5': '2026-10-30',
    'ai-chat.floating.prompts.dashboard.8': '2026-10-30',
    'ai-chat.floating.prompts.contacts.7': '2026-10-30',
    'ai-chat.floating.prompts.efforts.5': '2026-10-30',
    'ai-chat.floating.prompts.dashboard.9': '2026-11-06',
    'ai-chat.floating.prompts.contact.11': '2026-11-06',
    'ai-chat.floating.prompts.contacts.8': '2026-11-06',
    'ai-chat.floating.prompts.effort.8': '2026-11-06',
    'ai-chat.floating.prompts.efforts.6': '2026-11-06',
    'ai-chat.floating.prompts.group.6': '2026-11-06',
};

/** The badges a floating prompt row shows next to its text. Only New is assigned to floating prompts. */
export const getPromptBadges = (promptKey: string, today: Date = new Date()): PromptBadge[] =>
    isNew(NEW_UNTIL[promptKey], today) ? ['new'] : [];

/**
 * The i18n key of a prompt's short label, `ai-chat.floating.prompt-labels.<type>.<n>`. Only prompts
 * too long for the panel, or whose text carries steering instructions, have one; the rest display
 * their full text.
 */
export const getPromptLabelKey = (promptKey: string): string =>
    promptKey.replace(PROMPT_KEY_PREFIX, 'ai-chat.floating.prompt-labels.');

const TIME_PERIOD_PLACEHOLDER = 'time period';
const CASE_PLACEHOLDER = 'case number or name';
const CLIENT_PLACEHOLDER = 'client name';
const GROUP_PLACEHOLDER = 'group name';

// Prompts whose text carries a placeholder picked from chips — `[time period]` unless named otherwise
// — mapped to their option set (the full period list by default) and the value the picker opens
// pre-filled with. A default of the prompt's own that its set does not carry ("the last 30 days",
// "this quarter") is offered as an extra chip. A set replaces the period lists wherever those do not read
// right ("in the next [time period]"). `placeholderInputTypes` names the prompt's other placeholders that
// an autocomplete fills.
const CHIP_PICKER_PROMPTS: Record<
    string,
    {
        defaultValue: string;
        options?: PromptOption[];
        placeholder?: string;
        placeholderInputTypes?: PlaceholderFillConfig['placeholderInputTypes'];
    }
> = {
    'ai-chat.floating.prompts.dashboard.1': { defaultValue: 'this week' },
    'ai-chat.floating.prompts.dashboard.2': { defaultValue: 'this week' },
    'ai-chat.floating.prompts.dashboard.3': { defaultValue: 'last month' },
    'ai-chat.floating.prompts.dashboard.4': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.dashboard.5': { defaultValue: 'this year' },
    'ai-chat.floating.prompts.contact.5': { defaultValue: 'the past 2 years', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.contacts.1': { defaultValue: 'the past 6 months' },
    'ai-chat.floating.prompts.contacts.2': { defaultValue: 'this year' },
    'ai-chat.floating.prompts.contacts.3': { defaultValue: 'this year' },
    'ai-chat.floating.prompts.matters.2': { defaultValue: 'the last 3 months' },
    'ai-chat.floating.prompts.matters.4': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.matters.5': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.invoices.3': {
        defaultValue: '60 days',
        options: OVERDUE_DAYS_OPTIONS,
        placeholder: 'days',
    },
    'ai-chat.floating.prompts.invoices.4': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.invoices.5': {
        defaultValue: 'this year',
        options: TIME_PERIOD_OPTIONS_MONTH,
        placeholderInputTypes: { [CLIENT_PLACEHOLDER]: 'contact' },
    },
    'ai-chat.floating.prompts.timecards.1': { defaultValue: 'this week' },
    'ai-chat.floating.prompts.timecards.2': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.timecards.4': { defaultValue: 'last month' },
    'ai-chat.floating.prompts.timecards.5': {
        defaultValue: 'the last calendar year',
        options: TIME_PERIOD_OPTIONS_MONTH,
    },
    'ai-chat.floating.prompts.efforts.1': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.efforts.2': { defaultValue: 'the past 6 months' },
    'ai-chat.floating.prompts.efforts.3': { defaultValue: 'this quarter' },
    'ai-chat.floating.prompts.efforts.4': { defaultValue: 'the last 30 days' },
    'ai-chat.floating.prompts.employee.1': { defaultValue: 'the last 30 days' },
    'ai-chat.floating.prompts.employee.5': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.employee.6': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.employees.1': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.employees.2': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.employees.3': { defaultValue: 'this week' },
    'ai-chat.floating.prompts.group.4': { defaultValue: '6 months', options: LOOKBACK_OPTIONS_STANDARD },
    'ai-chat.floating.prompts.team.2': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.team.3': { defaultValue: 'this quarter' },
    'ai-chat.floating.prompts.teams.1': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.teams.2': { defaultValue: 'this month' },
    'ai-chat.floating.prompts.teams.4': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.office.4': { defaultValue: 'this month', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.offices.2': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.offices.3': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.job-roles.2': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.pricelists.1': { defaultValue: '3 months', options: LOOKBACK_OPTIONS },
    'ai-chat.floating.prompts.matters.9': { defaultValue: '12 months', options: LOOKBACK_OPTIONS },
    'ai-chat.floating.prompts.experience-records.3': { defaultValue: '12 months', options: LOOKBACK_OPTIONS },
    'ai-chat.floating.prompts.matters.10': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.matters.11': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.contacts.6': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.dashboard.7': { defaultValue: 'this year' },
    'ai-chat.floating.prompts.practice-area.5': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.practice-areas.3': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.office.6': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
    'ai-chat.floating.prompts.offices.4': { defaultValue: 'this year', options: TIME_PERIOD_OPTIONS_MONTH },
};

// Prompts whose text carries a `[case number or name]` placeholder, filled by the case autocomplete
// rather than a fixed option list — the same picker the main AI page's case cards use.
const CASE_PICKER_PROMPTS = ['ai-chat.floating.prompts.matters.6'];

// Prompts whose only placeholder is `[group name]`, filled by the group autocomplete.
const GROUP_PICKER_PROMPTS = ['ai-chat.floating.prompts.groups.1'];

// Prompts whose only placeholder is `[client name]`, filled by the contact autocomplete.
const CONTACT_PICKER_PROMPTS = [
    'ai-chat.floating.prompts.dashboard.9',
    'ai-chat.floating.prompts.contacts.8',
    'ai-chat.floating.prompts.efforts.6',
];

// Prompts whose placeholder is typed rather than picked — a topic, a keyword, an office name. No options
// and no input type, so the fill bar offers a plain text field.
const FREE_TEXT_PROMPTS = [
    'ai-chat.floating.prompts.matter.8',
    'ai-chat.floating.prompts.matters.7',
    'ai-chat.floating.prompts.matters.8',
    'ai-chat.floating.prompts.experience-records.1',
    'ai-chat.floating.prompts.teams.5',
    'ai-chat.floating.prompts.timecards.6',
];

interface PromptFill {
    /** Fixed chips, for placeholders picked from a list. Absent when an autocomplete fills it. */
    optionsMap?: Record<string, PromptOption[]>;
    config: PlaceholderFillConfig;
}

/**
 * Picker configuration for a prompt's placeholder, or null for prompts that carry none. Feeds the
 * same `usePlaceholderFill` flow the main AI page uses.
 */
export const getPromptFill = (promptKey: string): PromptFill | null => {
    if (CASE_PICKER_PROMPTS.includes(promptKey)) {
        return { config: { placeholderInputTypes: { [CASE_PLACEHOLDER]: 'matter' } } };
    }

    if (CONTACT_PICKER_PROMPTS.includes(promptKey)) {
        return { config: { placeholderInputTypes: { [CLIENT_PLACEHOLDER]: 'contact' } } };
    }

    if (GROUP_PICKER_PROMPTS.includes(promptKey)) {
        return { config: { placeholderInputTypes: { [GROUP_PLACEHOLDER]: 'group' } } };
    }

    if (FREE_TEXT_PROMPTS.includes(promptKey)) {
        return { config: {} };
    }

    const prompt = CHIP_PICKER_PROMPTS[promptKey];
    if (!prompt) {
        return null;
    }

    const { defaultValue, options: standardOptions = TIME_PERIOD_OPTIONS_FULL } = prompt;
    const placeholder = prompt.placeholder ?? TIME_PERIOD_PLACEHOLDER;
    // Offering an out-of-set default alongside keeps the starting value highlighted as the selected
    // chip, and reselectable after trying another value.
    const options = standardOptions.some((option) => option.value === defaultValue)
        ? standardOptions
        : [...standardOptions, { label: defaultValue[0].toUpperCase() + defaultValue.slice(1), value: defaultValue }];

    return {
        optionsMap: { [placeholder]: options },
        config: {
            defaults: { [placeholder]: defaultValue },
            ...(prompt.placeholderInputTypes && { placeholderInputTypes: prompt.placeholderInputTypes }),
        },
    };
};

/**
 * The prompt as it reads in the suggested list: a chip placeholder resolved to its starting value, so
 * the entry is a concrete question rather than a template. Mirrors the AI page and the phone, whose prompts
 * show a plain title and `previewText` while the prompt behind them carries the placeholder. Feed the raw prompt —
 * not this — to `usePlaceholderFill`, which needs the placeholder to open the picker. `label` is the
 * prompt's short label when it has one, which carries the same placeholder.
 *
 * A `[case number or name]`, a `[client name]` or a typed `[topic]` stays as it is: no single value stands in
 * for them the way "this week" stands in for a period, and the visible placeholder is what says one has to be
 * given.
 */
export const getPromptDisplayText = (promptKey: string, label: string): string => {
    const prompt = CHIP_PICKER_PROMPTS[promptKey];
    return prompt ? label.split(`[${prompt.placeholder ?? TIME_PERIOD_PLACEHOLDER}]`).join(prompt.defaultValue) : label;
};
